import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import LiveClock from './LiveClock'
import { portfolioData } from '@/data/portfolio'

const menuItems: Array<{ index: string; label: string; href: string }> = [
  { index: '01', label: 'What I build', href: '/#p-services' },
  { index: '02', label: 'Work', href: '/work' },
  { index: '03', label: 'Playground', href: '/playground' },
  { index: '04', label: 'Articles', href: '/blog' },
  { index: '05', label: 'Experience', href: '/#p-experience' },
  { index: '06', label: 'Contact', href: '/#p-contact' },
]

export default function PreviewNav() {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const closeMenu = useCallback(() => {
    setOpen(false)
    window.requestAnimationFrame(() => menuButtonRef.current?.focus())
  }, [])

  useEffect(() => {
    if (open) {
      document.body.classList.add('preview-menu-lock')
      closeRef.current?.focus()
    } else {
      document.body.classList.remove('preview-menu-lock')
    }
    return () => document.body.classList.remove('preview-menu-lock')
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeMenu()
        return
      }
      if (e.key === 'Tab') {
        const focusable = Array.from(
          menuRef.current?.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ) ?? [],
        )
        const first = focusable[0]
        const last = focusable.at(-1)
        if (!first || !last) return
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeMenu, open])

  return (
    <>
      <header className="preview-nav">
        <div className="container-custom flex h-14 items-center justify-between gap-4 md:h-16">
          <Link
            to="/"
            onClick={(event) => {
              if (pathname === '/') {
                event.preventDefault()
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }}
            className="preview-mono focus-ring text-[11px] font-bold uppercase tracking-[0.26em] text-cream-50"
          >
            Suriyaprakash<span className="text-sage-200">.</span>
          </Link>
          <p className="preview-mono hidden text-[10px] font-bold uppercase tracking-[0.22em] text-cream-50/75 lg:block">
            Erode, IN — <LiveClock /> IST
          </p>
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="preview-mono hidden items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sage-200 sm:flex">
              <span className="preview-pulse-dot h-1.5 w-1.5 rounded-full bg-sage-200" />
              Projects + roles
            </span>
            <a
              href={`mailto:${portfolioData.personal.email}?subject=Project%20enquiry`}
              className="preview-mono focus-ring hidden text-[10px] font-bold uppercase tracking-[0.16em] text-sage-200 md:inline-flex"
            >
              Discuss a project
            </a>
            <a
              href={portfolioData.personal.resume}
              target="_blank"
              rel="noopener noreferrer"
              className="preview-mono focus-ring hidden items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50 transition-colors hover:bg-white/10 md:inline-flex"
            >
              Resume
              <ArrowUpRight className="h-3 w-3" />
            </a>
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls="preview-menu"
              className="preview-mono focus-ring inline-flex items-center gap-1.5 rounded-full bg-sage-200 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-forest-strong transition-colors hover:bg-cream-50"
            >
              <Menu className="h-3 w-3" />
              Menu
            </button>
          </div>
        </div>
      </header>

      <div
        ref={menuRef}
        id="preview-menu"
        className={`preview-menu ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!open}
      >
        <div
          className="preview-blueprint absolute inset-0 opacity-60"
          aria-hidden="true"
        />
        <div className="preview-menu-content container-custom relative flex flex-col">
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 md:h-16">
            <span className="preview-mono text-[11px] font-bold uppercase tracking-[0.26em] text-cream-50">
              Menu
            </span>
            <button
              ref={closeRef}
              type="button"
              onClick={closeMenu}
              className="preview-mono focus-ring inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-cream-50 transition-colors hover:bg-white/10"
            >
              Close
              <X className="h-3 w-3" />
            </button>
          </div>

          <nav
            aria-label="Main navigation"
            className="preview-menu-items grid min-h-0 flex-1"
          >
            {menuItems.map((item, index) => {
              const rowClass =
                'preview-menu-link focus-ring group grid min-h-0 grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-white/10 sm:grid-cols-[3rem_minmax(0,1fr)_auto] sm:gap-5'
              const style = {
                transitionDelay: open ? `${140 + index * 60}ms` : '0ms',
              }
              const content = (
                <>
                  <span className="preview-mono text-[10px] font-bold text-sage-200 sm:text-xs">
                    {item.index}
                  </span>
                  <span className="preview-menu-label font-heading leading-none tracking-[-0.03em] transition-all duration-300 group-hover:translate-x-2 group-hover:text-sage-200 group-hover:italic">
                    {item.label}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-sage-200 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 sm:h-6 sm:w-6" />
                </>
              )
              return item.href.includes('#') ? (
                <a
                  key={item.index}
                  href={item.href}
                  onClick={closeMenu}
                  className={rowClass}
                  style={style}
                >
                  {content}
                </a>
              ) : (
                <Link
                  key={item.index}
                  to={item.href}
                  onClick={closeMenu}
                  aria-current={
                    pathname.startsWith(item.href) ? 'page' : undefined
                  }
                  className={rowClass}
                  style={style}
                >
                  {content}
                </Link>
              )
            })}
          </nav>

          <div className="shrink-0 py-1 sm:py-2">
            <div className="grid grid-cols-4 items-center gap-2 sm:flex sm:flex-wrap sm:gap-x-6">
              {portfolioData.personal.socials.map((social) => (
                <a
                  key={social.platform}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="preview-link-sweep preview-mono focus-ring inline-flex min-h-11 items-center gap-1 text-[9px] font-bold uppercase tracking-[0.08em] text-cream-50/85 transition-colors hover:text-sage-200 sm:text-[10px] sm:tracking-[0.18em]"
                >
                  {social.platform}
                  <ArrowUpRight className="h-2.5 w-2.5" />
                </a>
              ))}
              <a
                href={portfolioData.personal.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="preview-link-sweep preview-mono focus-ring inline-flex min-h-11 items-center gap-1 text-[9px] font-bold uppercase tracking-[0.08em] text-sage-200 sm:text-[10px] sm:tracking-[0.18em]"
              >
                Resume
                <ArrowUpRight className="h-2.5 w-2.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
